<script setup lang="ts">
import { slideInjectionKey } from '@/core/injection-keys.ts';
import { YoutubePlayerProps } from '@/shared/components/base/YoutubePlayer/types.ts';
import { PlayerEvent } from '@/shared/composables/playersControl/useBackgroundAudioControl.ts';
import { useYouTubePlayerControl } from '@/shared/composables/playersControl/useYouTubePlayerControl.ts';
import { MaybeNull } from '@/shared/types';
import { createScopedEventName } from '@/shared/utils/custom-events.ts';
import {
  computed, inject, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';

const props = defineProps<YoutubePlayerProps>();

const iframe$ = ref<MaybeNull<HTMLIFrameElement>>(null);
const slide = inject(slideInjectionKey, null);

const postCommand = (command: string, extra?: Record<string, unknown>) => {
  iframe$.value?.contentWindow?.postMessage({
    command,
    ...extra,
  }, '*');
};

const player = ref<MaybeNull<{
  mute: () => void;
  unMute: () => void;
  cueVideoById: (videoId: string) => void;
  destroy: () => void;
}>>(null);

const onMessage = (event: MessageEvent) => {
  if (!event.data || typeof event.data !== 'object') {
    return;
  }

  const { type, data } = event.data;

  if (type === 'stateChange' && slide?.value) {
    if (data === 1) {
      window.dispatchEvent(new CustomEvent(createScopedEventName(PlayerEvent.Play, slide.value.id)));
    } else if (data === 2) {
      window.dispatchEvent(new CustomEvent(createScopedEventName(PlayerEvent.Pause, slide.value.id)));
    }
  }
};

onMounted(() => {
  player.value = {
    mute: () => postCommand('mute'),
    unMute: () => postCommand('unmute'),
    cueVideoById: (videoId: string) => postCommand('cueVideo', {
      videoId,
    }),
    destroy: () => postCommand('destroy'),
  };

  window.addEventListener('message', onMessage);
});

watch(() => props.videoId, (newId) => {
  if (newId) {
    postCommand('cueVideo', {
      videoId: newId,
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  postCommand('destroy');
});

useYouTubePlayerControl({
  player,
  manualMute: props.manualMute,
});

const bridgeUrl = computed(() => {
  const base = import.meta.env.VITE_APP_HTTP;
  const params = new URLSearchParams({
    v: props.videoId,
    autoplay: props.autoplay ? '1' : '0',
    playsinline: '1',
  });

  return `${base}/youtube.html?${params.toString()}`;
});
</script>

<template>
  <div class="video">
    <iframe
      ref="iframe$"
      :src="bridgeUrl"
      frameborder="0"
      allow="autoplay; fullscreen"
      allowfullscreen
      referrerpolicy="origin"
    />
  </div>
</template>

<style scoped>
.video {
  width: 100%;
  height: 100%;
}

iframe {
  width: 100%;
  aspect-ratio: 16 / 9;
  border: none;
}
</style>
